import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Alert,
  useTheme,
  useMediaQuery,
  Divider,
  IconButton,
} from '@mui/material';
import {
  Email,
  Phone,
  WhatsApp,
  AccessTime,
  Send,
  LocationOn,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { MessageCenter } from '../components/MessageCenter';

const MotionBox = motion(Box);
const MotionCard = motion(Card);

export const Contact: React.FC = () => {
  const { currentUser } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [formData, setFormData] = useState({
    name: currentUser?.displayName || '',
    email: currentUser?.email || '',
    subject: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Update form data when user authentication changes
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      name: currentUser?.displayName || '',
      email: currentUser?.email || '',
    }));
  }, [currentUser]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const { name, email, subject, message } = formData;
    
    if (!name || !email || !subject || !message) {
      setError('Please fill in all fields');
      return;
    }

    try {
      setError('');
      setLoading(true);
      
      // Save message to Firestore
      const messageData: any = {
        name,
        email,
        subject,
        message,
        timestamp: serverTimestamp(),
        status: 'unread',
      };

      // Include user ID if user is authenticated
      if (currentUser) {
        messageData.userId = currentUser.uid;
      }

      await addDoc(collection(db, 'contact_messages'), messageData);

      setSuccess(true);
      setFormData({
        name: currentUser?.displayName || '',
        email: currentUser?.email || '',
        subject: '',
        message: '',
      });
      
      // Trigger refresh of MessageCenter for authenticated users
      if (currentUser) {
        setRefreshTrigger(prev => prev + 1);
      }
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(false), 5000);
    } catch (error: any) {
      setError('Failed to send message. Please try again.');
      console.error('Error saving message:', error);
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: <Email />,
      title: 'Email Support',
      value: 'support@t1fxteam.co.za',
      link: 'mailto:support@t1fxteam.co.za',
      description: 'Send us an email for detailed inquiries',
    },
    {
      icon: <Phone />,
      title: 'Call Center',
      value: '0870 126 287',
      link: 'tel:0870126287',
      description: 'Speak directly with our support team',
    },
    {
      icon: <WhatsApp />,
      title: 'WhatsApp',
      value: '+27 071 394 4924',
      link: 'https://wa.me/27713944924',
      description: 'Quick support via WhatsApp',
    },
    {
      icon: <AccessTime />,
      title: 'Business Hours',
      value: '9:00 AM - 5:00 PM',
      link: null,
      description: 'Monday to Friday (SAST)',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
    },
  };

  return (
    <Box sx={{ minHeight: '100vh', pt: { xs: 10, md: 12 }, pb: 8 }}>
      <Container maxWidth="lg">
        <MotionBox
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          {/* Header Section */}
          <motion.div variants={itemVariants}>
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Typography
                variant={isMobile ? 'h3' : 'h2'}
                component="h1"
                gutterBottom
                sx={{
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontWeight: 800,
                  mb: 3,
                }}
              >
                Contact Us
              </Typography>
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ maxWidth: '600px', mx: 'auto', mb: 2 }}
              >
                Get in touch with our expert team for support, inquiries, or to learn more about our trading solutions.
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ maxWidth: '500px', mx: 'auto' }}
              >
                Need assistance outside business hours? Send us a message and we'll get back to you as soon as possible.
              </Typography>
            </Box>
          </motion.div>

          {/* Message Center for Authenticated Users */}
          {currentUser && (
            <motion.div variants={itemVariants}>
              <MessageCenter refreshTrigger={refreshTrigger} />
            </motion.div>
          )}

          <Grid container spacing={6}>
            {/* Contact Form */}
            <Grid size={{ xs: 12, md: 7 }}>
              <motion.div variants={itemVariants}>
                <MotionCard
                  variants={itemVariants}
                  sx={{
                    p: { xs: 3, md: 4 },
                    background: 'rgba(255, 255, 255, 0.05)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 215, 0, 0.2)',
                  }}
                >
                  <CardContent>
                    <Typography
                      variant="h4"
                      component="h2"
                      gutterBottom
                      sx={{ 
                        color: 'primary.main',
                        fontWeight: 700,
                        mb: 3,
                      }}
                    >
                      Send Us a Message
                    </Typography>

                    {success && (
                      <Alert severity="success" sx={{ mb: 3 }}>
                        Thank you! Your message has been sent successfully. We'll get back to you soon.
                      </Alert>
                    )}

                    {error && (
                      <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                      </Alert>
                    )}

                    <Box component="form" onSubmit={handleSubmit}>
                      <Grid container spacing={3}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            fullWidth
                            label="Full Name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            variant="outlined"
                            disabled={!!currentUser}
                            helperText={currentUser ? "Auto-filled from your profile" : ""}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            fullWidth
                            label="Email Address"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            variant="outlined"
                            disabled={!!currentUser}
                            helperText={currentUser ? "Auto-filled from your profile" : ""}
                          />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                          <TextField
                            fullWidth
                            label="Subject"
                            name="subject"
                            value={formData.subject}
                            onChange={handleChange}
                            required
                            variant="outlined"
                          />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                          <TextField
                            fullWidth
                            label="Message"
                            name="message"
                            value={formData.message}
                            onChange={handleChange}
                            required
                            multiline
                            rows={6}
                            variant="outlined"
                            placeholder="Tell us about your inquiry, support needs, or how we can help you..."
                          />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                          <Button
                            type="submit"
                            variant="contained"
                            size="large"
                            disabled={loading}
                            startIcon={<Send />}
                            sx={{
                              px: 4,
                              py: 1.5,
                              fontSize: '1.1rem',
                              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                              color: '#000000',
                              fontWeight: 700,
                              '&:hover': {
                                background: `linear-gradient(45deg, ${theme.palette.primary.dark}, #996F08)`,
                                transform: 'translateY(-2px)',
                              },
                              '&:disabled': {
                                background: 'rgba(255, 215, 0, 0.3)',
                                color: 'rgba(0, 0, 0, 0.5)',
                              },
                            }}
                          >
                            {loading ? 'Sending...' : 'Send Message'}
                          </Button>
                        </Grid>
                      </Grid>
                    </Box>
                  </CardContent>
                </MotionCard>
              </motion.div>
            </Grid>

            {/* Contact Information */}
            <Grid size={{ xs: 12, md: 5 }}>
              <motion.div variants={itemVariants}>
                <Grid container spacing={3}>
                  {contactInfo.map((contact, index) => (
                    <Grid key={index} size={{ xs: 12 }}>
                      <MotionCard
                        variants={itemVariants}
                        sx={{
                          background: 'rgba(255, 255, 255, 0.03)',
                          border: '1px solid rgba(255, 215, 0, 0.1)',
                          transition: 'all 0.3s ease',
                          cursor: contact.link ? 'pointer' : 'default',
                          '&:hover': contact.link ? {
                            background: 'rgba(255, 215, 0, 0.1)',
                            transform: 'translateY(-2px)',
                          } : {},
                        }}
                        onClick={() => contact.link && window.open(contact.link, '_blank')}
                      >
                        <CardContent sx={{ p: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            <Box
                              sx={{
                                color: 'primary.main',
                                mt: 0.5,
                                '& svg': { fontSize: '1.5rem' },
                              }}
                            >
                              {contact.icon}
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Typography
                                variant="h6"
                                component="h3"
                                sx={{ 
                                  fontWeight: 600,
                                  color: 'text.primary',
                                  mb: 0.5,
                                }}
                              >
                                {contact.title}
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{ 
                                  color: 'primary.main',
                                  fontWeight: 600,
                                  mb: 0.5,
                                }}
                              >
                                {contact.value}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {contact.description}
                              </Typography>
                            </Box>
                          </Box>
                        </CardContent>
                      </MotionCard>
                    </Grid>
                  ))}
                </Grid>
              </motion.div>

              {/* Additional Information */}
              <MotionCard
                variants={itemVariants}
                sx={{
                  mt: 4,
                  background: 'rgba(255, 215, 0, 0.1)',
                  border: '1px solid rgba(255, 215, 0, 0.3)',
                }}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    component="h3"
                    sx={{ 
                      color: 'primary.main',
                      fontWeight: 600,
                      mb: 2,
                    }}
                  >
                    Support Information
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    <strong>Business Hours:</strong> Monday to Friday, 9:00 AM - 5:00 PM (SAST)
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    For urgent matters outside business hours, please send us a message or contact us via WhatsApp. 
                    We strive to respond to all inquiries within 24 hours.
                  </Typography>
                </CardContent>
              </MotionCard>
            </Grid>
          </Grid>
        </MotionBox>
      </Container>
    </Box>
  );
}; 